/**
 * `useRosterRepair` — the minimal-change re-solve behind *Fix and re-solve*
 * (#1960; endpoint #1954).
 *
 *   * `useProposeRepair` → `POST /api/teams/{team_id}/roster/repair
 *     {from, to, pins, max_shifts_moved, max_days_forward, more_time,
 *     fill_old_gaps}` — re-solves the dates around the pins with the fewest
 *     changed cells and returns a draft (writes nothing), or `status:
 *     "reach_exceeded"` / `"timed_out"` with no draft (#1997). #1998: the
 *     re-solve runs as a job on the solver worker. The POST answers 202
 *     `{job_id, state}` (a refusal that needs no solve still answers the POST
 *     itself); the hook then polls `GET …/roster/repair/jobs/{job_id}` about
 *     once a second until `done` — it resolves to the same `RepairResponse`
 *     — or `failed`, which rejects with the job's sentence
 *     (`RepairJobFailedError`). `progress` is the job's state while it runs;
 *     `cancel()` (and unmounting) stops the polling. #2004: a run that ends
 *     before its job did — `cancel()`, unmount, a new run, or a poll that
 *     gave up — also stops the job on the worker (`DELETE …/repair/jobs/
 *     {job_id}`, `stopRepairJob`), so the next solve does not wait behind
 *     it. Fire-and-forget: never awaited, every error swallowed (an API
 *     older than the SPA answers 404/405, and that shows nothing).
 *   * `useApplyRepair` → `POST …/repair/{draft_id}/apply {skip, seat,
 *     absences}` — writes the reviewed draft; 409 when the roster or a pin
 *     moved since.
 *   * `useRepairReach` → `GET …/roster/repair/reach` — the team's reach
 *     defaults (#1987): shifts moved (a third of the team unless set) and
 *     days forward, which step 2 prefills.
 *
 * The apply invalidates every read that shows the consequence, for each
 * window the repair touched: the roster (cycle + team range), the day's
 * ladder, the Review cover inbox, Coverage, and the leave reads (an
 * absence row may have been recorded).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";

import { useAuth } from "../lib/AuthContext";
import { ApiError, apiFetch } from "./client";
import type { components } from "./schema.gen";
import { coverageReviewQueryFilter } from "./useCoverageReview";
import { cycleRosterQueryFilter } from "./useCycleRoster";
import { mcAttentionQueryKey } from "./useMcAttention";
import { shortfallOptionsQueryFilter } from "./useShortfallOptions";

export type RepairPin = components["schemas"]["RepairPin"];
export type RepairResponse = components["schemas"]["RepairResponse"];
export type RepairChange = components["schemas"]["RepairChangeOut"];
export type RepairApplyBody = components["schemas"]["RepairApplyRequest"];
export type RepairApplyResponse = components["schemas"]["RepairApplyResponse"];
export type RepairReachDefaults = components["schemas"]["RepairReachOut"];

export interface ProposeRepairVars {
  from: string;
  to: string;
  pins: RepairPin[];
  /** #1987 — this repair's reach; omitted = the team default. */
  max_shifts_moved?: number;
  max_days_forward?: number;
  /** #1997 — *Run again with more time*: twice the first run's time a window
   *  (#2029: the first run scales with people × dates; at least 30 s). */
  more_time?: boolean;
  /** Founder 3 Oct 2026 — also fill seats the roster was already short of. */
  fill_old_gaps?: boolean;
}

export type RepairJob = components["schemas"]["RepairJobOut"];
export type RepairJobStatus = components["schemas"]["RepairJobStatusOut"];
export type RepairJobState = RepairJob["state"];

/** How often the job is read while it runs (#1998). */
export const REPAIR_POLL_MS = 1000;
/** Seconds a job may wait queued before the panel says the solver is starting. */
export const REPAIR_QUEUED_HINT_S = 15;
/** The sentence for a job the server no longer has (the API's own words). */
export const REPAIR_JOB_LOST_TEXT =
  "The re-solve stopped before it finished. Run it again, or re-solve fewer dates.";
// Reads that fail in transit (network, 5xx) in a row before the run gives up.
const MAX_POLL_ERRORS = 3;

/** A repair job that ended `failed`: a refusal the solve found, or the
 * sentence for a job the worker never finished. */
export class RepairJobFailedError extends Error {
  readonly reason: string;
  readonly detail: Record<string, unknown>;

  constructor(error: { reason: string; message: string; [key: string]: unknown }) {
    super(error.message);
    this.name = "RepairJobFailedError";
    this.reason = error.reason;
    this.detail = error;
  }
}

/** The run in flight: its job's last state and when Run was pressed (ms). */
export interface RepairRunProgress {
  state: RepairJobState;
  startedAt: number;
}

function aborted(): DOMException {
  return new DOMException("The repair run was stopped.", "AbortError");
}

function pause(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(aborted());
      return;
    }
    const onAbort = () => {
      window.clearTimeout(timer);
      reject(aborted());
    };
    const timer = window.setTimeout(() => {
      signal.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    signal.addEventListener("abort", onAbort, { once: true });
  });
}

/** Read a repair job until it is done (its response) or failed (throws). */
export async function pollRepairJob(
  teamId: string,
  jobId: string,
  opts: { signal: AbortSignal; pollMs?: number; onState?: (state: RepairJobState) => void },
): Promise<RepairResponse> {
  const { signal, pollMs = REPAIR_POLL_MS, onState } = opts;
  let errors = 0;
  for (;;) {
    let status: RepairJobStatus;
    try {
      status = await apiFetch<RepairJobStatus>(`/api/teams/${teamId}/roster/repair/jobs/${jobId}`, {
        signal,
      });
      errors = 0;
    } catch (err) {
      if (signal.aborted) throw err;
      // Gone (past its hour, or never stored): it did not finish.
      if (err instanceof ApiError && err.status === 404) {
        throw new RepairJobFailedError({
          reason: "repair_job_lost",
          message: REPAIR_JOB_LOST_TEXT,
        });
      }
      if (err instanceof ApiError && err.status < 500) throw err;
      errors += 1;
      if (errors >= MAX_POLL_ERRORS) throw err;
      await pause(pollMs, signal);
      continue;
    }
    if (signal.aborted) throw aborted();
    onState?.(status.state);
    if (status.state === "done" && status.response) return status.response;
    if (status.state === "failed") {
      throw new RepairJobFailedError(
        status.error ?? { reason: "repair_job_lost", message: REPAIR_JOB_LOST_TEXT },
      );
    }
    await pause(pollMs, signal);
  }
}

/**
 * Stop a repair job nobody waits for any more (#2004): the API cancels it
 * while queued or stops its work-horse while it runs. Fire-and-forget —
 * never awaited, every error swallowed: an API older than the SPA answers
 * 404/405, and a failed stop must show nothing.
 */
export function stopRepairJob(teamId: string, jobId: string): void {
  void apiFetch<null>(`/api/teams/${teamId}/roster/repair/jobs/${jobId}`, {
    method: "DELETE",
  }).catch(() => undefined);
}

export function useProposeRepair(teamId: string, opts: { pollMs?: number } = {}) {
  const { pollMs } = opts;
  const run = useRef<AbortController | null>(null);
  const [progress, setProgress] = useState<RepairRunProgress | null>(null);
  const mutation = useMutation<RepairResponse, ApiError | Error, ProposeRepairVars>({
    mutationFn: async (vars) => {
      run.current?.abort();
      const ctl = new AbortController();
      run.current = ctl;
      const startedAt = Date.now();
      setProgress({ state: "queued", startedAt });
      // #2004 — the job this run started, and whether it is over (done or
      // failed: nothing left to stop).
      let jobId: string | null = null;
      let over = false;
      try {
        // Not aborted by the signal: a run stopped while its post is in
        // flight still learns its job, so that job can be stopped too.
        const job = await apiFetch<RepairJob>(`/api/teams/${teamId}/roster/repair`, {
          method: "POST",
          body: JSON.stringify(vars),
        });
        jobId = job.job_id;
        over = job.state === "done" || job.state === "failed";
        if (ctl.signal.aborted) throw aborted();
        const res = await pollRepairJob(teamId, job.job_id, {
          signal: ctl.signal,
          ...(pollMs !== undefined ? { pollMs } : {}),
          onState: (state) => {
            if (run.current === ctl) setProgress({ state, startedAt });
          },
        });
        over = true;
        return res;
      } catch (err) {
        // The job failed, or the server no longer has it.
        if (err instanceof RepairJobFailedError) over = true;
        throw err;
      } finally {
        // Stopped (cancel, unmount, a new run) or gave up reading it before
        // the job ended: nobody will read it, so it should not hold the
        // solver.
        if (jobId && !over) stopRepairJob(teamId, jobId);
        if (run.current === ctl) {
          run.current = null;
          setProgress(null);
        }
      }
    },
  });
  // Unmounting stops the polling, and with it the job (#2004).
  useEffect(() => () => run.current?.abort(), []);
  const { reset } = mutation;
  const cancel = useCallback(() => {
    run.current?.abort();
    run.current = null;
    setProgress(null);
    reset();
  }, [reset]);
  return { ...mutation, progress, cancel };
}

export function repairReachQueryKey(teamId: string, accountId: string | null) {
  return ["repair-reach", teamId, accountId] as const;
}

/** The team's *Fix and re-solve* reach defaults (#1987). */
export function useRepairReach(teamId: string | undefined) {
  const { accountId } = useAuth();
  return useQuery({
    queryKey: repairReachQueryKey(teamId ?? "", accountId),
    enabled: Boolean(teamId && accountId),
    staleTime: 60_000,
    retry: false,
    queryFn: () => apiFetch<RepairReachDefaults>(`/api/teams/${teamId}/roster/repair/reach`),
  });
}

export interface ApplyRepairVars {
  draftId: string;
  body: RepairApplyBody;
  /** Windows to refresh besides the ones the response names. */
  cycleIds: string[];
}

export function useApplyRepair(teamId: string) {
  const qc = useQueryClient();
  return useMutation<RepairApplyResponse, ApiError | Error, ApplyRepairVars>({
    mutationFn: ({ draftId, body }) =>
      apiFetch<RepairApplyResponse>(`/api/teams/${teamId}/roster/repair/${draftId}/apply`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: (res, vars) => {
      const cycles = new Set([...vars.cycleIds, ...res.per_window.map((w) => w.cycle_id)]);
      // Every window's roster in one call: the shared reads refetch once.
      qc.invalidateQueries(cycleRosterQueryFilter(cycles));
      for (const cycleId of cycles) {
        qc.invalidateQueries(shortfallOptionsQueryFilter(cycleId));
        qc.invalidateQueries({ queryKey: mcAttentionQueryKey(cycleId) });
        qc.invalidateQueries(coverageReviewQueryFilter(cycleId));
        qc.invalidateQueries({ queryKey: ["leave-review", cycleId] });
      }
      qc.invalidateQueries({
        predicate: (q) => q.queryKey[0] === "team-range-roster" && q.queryKey[2] === teamId,
      });
      qc.invalidateQueries({ queryKey: ["team-date-requests"] });
      // #2277 — an MC counts toward the daily leave-quota lines.
      qc.invalidateQueries({ queryKey: ["leave-quota"] });
    },
  });
}
